/**
 * Group lottery rounds by section type for lottery result page.
 * Matches structure from latte-result.html: หวยรัฐบาล, หวยธกส., หวยออมสิน, หวยชุด, หวยหุ้นไทย, หวยต่างประเทศ, หวยหุ้นต่างประเทศ.
 *
 * --- กฎหวยรัฐบาลไทย (สำหรับ query / แสดงผล) ---
 * - จำนวนรอบ: 2 รอบต่อเดือน — งวดวันที่ 1 และ งวดวันที่ 16
 * - งวด 1: รับแทง 20 เดือนนี้ 00:00 → 1 เดือนหน้า 15:30 (วันปิดรับแทง = วันประกาศผล = 1)
 * - งวด 16: รับแทง 5 เดือนนี้ 00:00 → 16 เดือนนี้ 15:30 (วันปิดรับแทง = วันประกาศผล = 16)
 * - ประกาศผล: 15:40 (ตามรอบ)
 * การเลือกรอบแสดงผล: ถ้า selectedDate อยู่ใน [betStartTime, betEndTime] ของรอบใด → แสดงรอบนั้น (ล่าสุด); ถ้าไม่มี → แสดงรอบล่าสุดของหวยรัฐบาลไทย (เรียงตาม betEndTime). แสดง "รอผล" เมื่อ round.status === 'open' หรือยังไม่มีผล
 */
import type { LotteryRound } from '../../services/lotteryService'

export type SectionKind = 'yeekee' | 'government' | 'set' | 'simple' | 'thai_stock' | 'foreign' | 'foreign_stock' | 'other'

export function isYeekeeRound(round: LotteryRound): boolean {
  const name = (round.huayTypeName || '') + (round.huayName || '') + (round.roundNumber || '') + (round.huayTypeCode || '')
  return /ยี่กี|ยี|yeekee/i.test(name)
}

export function isSetLottery(round: LotteryRound): boolean {
  const typeName = (round.huayTypeName || '').toLowerCase()
  const huayName = (round.huayName || '').toLowerCase()
  return typeName.includes('หวยชุด') || huayName.includes('ชุด')
}

export function isGovernmentLottery(round: LotteryRound): boolean {
  const huayName = (round.huayName || '').toLowerCase()
  const typeName = (round.huayTypeName || '').toLowerCase()
  if (huayName.includes('ชุด')) return false
  return (
    huayName.includes('รัฐบาล') ||
    huayName.includes('หวยรัฐบาล') ||
    typeName.includes('รัฐบาล')
  )
}

/** หวยธกส. (ธ.ก.ส.) */
export function isEgovRound(round: LotteryRound): boolean {
  const combined = (round.huayName || '') + (round.huayTypeName || '')
  return /ธ\.?\s*ก\.?\s*ส\.?|ธกส|egov/i.test(combined)
}

/** หวยออมสิน (GSB) */
export function isGsbRound(round: LotteryRound): boolean {
  const combined = (round.huayName || '') + (round.huayTypeName || '')
  return /ออมสิน|gsb/i.test(combined)
}

/** จับยี่กี VIP — ตรงกับ announce-results: ใช้ huayName + huayTypeName + roundNumber + huayTypeCode */
export function isYeekeeVipRound(round: LotteryRound): boolean {
  const combined = (
    (round.huayName || '') +
    (round.huayTypeName || '') +
    (round.roundNumber || '') +
    (round.huayTypeCode || '')
  ).toLowerCase()
  const isYeekee = /ยี่กี|ยี|yeekee/i.test(combined)
  const isVip = /vip|วีไอพี/i.test(combined)
  return isYeekee && isVip
}

/**
 * เฉพาะหวยรัฐบาลไทย (ไม่รวม หวย ธ.ก.ส., หวยออมสิน) — ใช้สำหรับ section ผลรางวัล "หวยรัฐบาล" เท่านั้น
 * เลือกรอบจาก: ชื่อมี "รัฐบาลไทย" หรือ "หวยรัฐบาลไทย" หรือรหัสรอบขึ้นต้นด้วย LGV (Government Lottery)
 */
export function isThaiGovernmentLotteryOnly(round: LotteryRound): boolean {
  if (!isGovernmentLottery(round)) return false
  const huayName = (round.huayName || '').toLowerCase()
  const typeName = (round.huayTypeName || '').toLowerCase()
  const roundNumber = (round.roundNumber || '').toUpperCase()
  const combined = huayName + typeName
  // ไม่เอา ธ.ก.ส. / ออมสิน
  if (/ธ\.?\s*ก\.?\s*ส\.?|ธกส|ออมสิน|egov|gsb/i.test(combined)) return false
  // เอา: ชื่อมี รัฐบาลไทย / หวยรัฐบาลไทย / หวยรัฐบาล หรือรหัสรอบเป็น LGV
  if (/รัฐบาลไทย|หวยรัฐบาลไทย|หวยรัฐบาล/.test(combined)) return true
  if (roundNumber.startsWith('LGV')) return true
  return false
}

/** Section key for grouping (used for ordering and grouping). */
export function getSectionKind(round: LotteryRound): SectionKind {
  if (isYeekeeRound(round)) return 'yeekee'
  if (isGovernmentLottery(round)) return 'government'
  if (isSetLottery(round)) return 'set'
  const typeName = (round.huayTypeName || '').toLowerCase()
  const name = (round.huayName || '').toLowerCase()
  if (typeName.includes('หุ้นไทย') || name.includes('หุ้นไทย')) return 'thai_stock'
  if (typeName.includes('หุ้นต่างประเทศ') || name.includes('หุ้นต่างประเทศ')) return 'foreign_stock'
  /* หวยหุ้น (ดาวน์โจนส์, นิเคอิ, จีน ฯลฯ) — ชื่อมี "หุ้น" แต่ไม่ใช่ "หุ้นไทย" */
  if (typeName.includes('หุ้น') || name.includes('หุ้น')) return 'foreign_stock'
  if (
    typeName.includes('ต่างประเทศ') ||
    typeName.includes('ต่างประเทศ') ||
    /ลาว|มาเลย์|ฮานอย|เวียดนาม|กัมพูชา/i.test(typeName + name)
  )
    return 'foreign'
  return 'simple'
}

/** Display order of section kinds (for list page). */
const SECTION_ORDER: SectionKind[] = [
  'government',
  'yeekee',
  'simple',
  'set',
  'thai_stock',
  'foreign',
  'foreign_stock',
  'other',
]

/** Section title by kind (fallback when using type name as title). */
export const SECTION_TITLES: Record<SectionKind, string> = {
  yeekee: 'จับยี่กี',
  government: 'หวยรัฐบาล',
  set: 'หวยชุด',
  simple: 'หวยไทย',
  thai_stock: 'หวยหุ้นไทย',
  foreign: 'หวยต่างประเทศ',
  foreign_stock: 'หวยหุ้นต่างประเทศ',
  other: 'ผลรางวัล',
}

export function getSectionOrder(kind: SectionKind): number {
  const i = SECTION_ORDER.indexOf(kind)
  return i >= 0 ? i : SECTION_ORDER.length
}

/** Group rounds by section. Returns array of { kind, sectionTitle, rounds }. Section title matches HTML (หวยรัฐบาล, หวยชุด, หวยหุ้นไทย, etc.). */
export function groupRoundsBySection(rounds: LotteryRound[]): Array<{ kind: SectionKind; sectionTitle: string; rounds: LotteryRound[] }> {
  const map = new Map<SectionKind, LotteryRound[]>()
  for (const r of rounds) {
    const kind = getSectionKind(r)
    if (!map.has(kind)) map.set(kind, [])
    map.get(kind)!.push(r)
  }
  const result: Array<{ kind: SectionKind; sectionTitle: string; rounds: LotteryRound[] }> = []
  for (const kind of SECTION_ORDER) {
    const list = map.get(kind)
    if (list && list.length > 0) {
      result.push({ kind, sectionTitle: SECTION_TITLES[kind], rounds: list })
    }
  }
  for (const [kind, list] of map) {
    if (SECTION_ORDER.includes(kind)) continue
    result.push({ kind, sectionTitle: SECTION_TITLES[kind], rounds: list })
  }
  return result
}
